import {
  BooleanField,
  BooleanInput,
  Datagrid,
  DeleteWithConfirmButton,
  EditButton,
  FunctionField,
  Create as RACreate,
  Edit as RAEdit,
  Filter as RAFilter,
  List as RAList,
  SimpleForm,
  TextField,
  TextInput,
  useRecordContext,
} from 'react-admin';
import { FormGroup, Tab } from '@mui/material';
import InsuranceDetails, { requiredFields } from './details';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  VehicleInsuranceRuleSet,
  VehicleInsuranceType,
} from '../../@generated/schemas';

import CustomPagination from '../../CustomPagination';
import InsuranceContent from './content';
import InsuranceContentVAG from './contentVAG';
import InsuranceField from './insuranceField';
import InsuranceRuleSets from './ruleSets';
import Reporting from './reporting';
import queryString from 'query-string';
import { transform } from '../../common/TranslationInput';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useTranslate } from '../../locales';

export const TABS = {
  details: 'details',
  content: 'content',
  vag: 'vag',
  links: 'links',
  ruleSetsTarifs: 'ruleSetsTarifs',
};

const Title = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  return (
    <span>
      {translate('admin.insurance')}: {record?.name}
    </span>
  );
};

const Form = () => {
  const { watch } = useFormContext();
  const record = useRecordContext();
  const { search } = useLocation();
  const translate = useTranslate();
  const [selectedTab, setTab] = useState<string>(
    (queryString.parse(search)?.tab || TABS.details) as string,
  );

  const vehicleInsuranceRuleSets: VehicleInsuranceRuleSet[] =
    watch('vehicleInsuranceRuleSets') || record?.vehicleInsuranceRuleSets;

  const onChangeTab = (tabName: string) => {
    if (tabName) {
      setTab(tabName);

      const url = new URL(window.location.href);
      url.searchParams.set('tab', tabName);
      window.history.pushState(
        {},
        '',
        `/#/VehicleInsuranceType/${record?.id || 'create'}${url.search}`,
      );
    }
  };

  return (
    <TabContext value={selectedTab}>
      <TabList
        defaultValue={selectedTab}
        onChange={(_, value) => onChangeTab(value)}>
        <Tab label={translate('admin.insuranceDetails')} value={TABS.details} />
        <Tab label={translate('admin.content')} value={TABS.content} />
        <Tab label={translate('admin.vag45')} value={TABS.vag} />
        <Tab label={translate('admin.links')} value={TABS.links} />
        {record?.id && (
          <Tab
            label={translate('admin.ruleSetsAndTariffs')}
            value={TABS.ruleSetsTarifs}
          />
        )}
      </TabList>

      <TabPanel value={TABS.details} style={{ width: '100%' }}>
        <FormGroup row>
          <BooleanInput
            source="isDefault"
            label={translate('admin.isDefault')}
            style={{ marginRight: 8 }}
          />
        </FormGroup>
        <InsuranceDetails vehicleInsuranceTypeId={record?.id as string}>
          <InsuranceField
            source="name"
            label={translate('admin.name')}
            requiredFields={requiredFields}
          />
          <InsuranceField
            source="policyNumber"
            label={translate('admin.policyNumber')}
            requiredFields={requiredFields}
          />
          <InsuranceField
            source="policyHolder"
            label={translate('admin.policyHolder')}
            requiredFields={requiredFields}
          />
          <InsuranceField
            source="postalAddress"
            label={translate('admin.postalAddress')}
            requiredFields={requiredFields}
          />
          <InsuranceField
            source="billingAddress"
            label={translate('admin.billingAddress')}
            requiredFields={requiredFields}
          />
          <InsuranceField
            source="phoneNumber"
            label={translate('admin.phoneNumber')}
            requiredFields={requiredFields}
          />
          <Reporting />
        </InsuranceDetails>
      </TabPanel>
      <TabPanel value={TABS.content} style={{ width: '100%' }}>
        <InsuranceContent source="content" />
      </TabPanel>
      <TabPanel value={TABS.vag} style={{ width: '100%' }}>
        <InsuranceContentVAG />
      </TabPanel>
      <TabPanel value={TABS.links} style={{ width: '100%' }}>
        <InsuranceContent source="contentLinks" />
      </TabPanel>
      {record?.id && (
        <TabPanel value={TABS.ruleSetsTarifs} style={{ width: '100%' }}>
          <InsuranceRuleSets
            vehicleInsuranceTypeId={record?.id as string}
            vehicleInsuranceRuleSets={vehicleInsuranceRuleSets}
          />
        </TabPanel>
      )}
    </TabContext>
  );
};

const sharedTransform = (data) => {
  const { content } = transform(data, 'content');
  const { contentVAG } = transform(data, 'contentVAG');
  const { contentLinks } = transform(data, 'contentLinks');

  return {
    ...data,
    content,
    contentVAG,
    contentLinks,
  };
};

export const InsuranceCreate = (props: any) => (
  <RACreate
    {...props}
    undoable="false"
    mutationMode="pessimistic"
    transform={sharedTransform}>
    <SimpleForm defaultValues={{ isDefault: false }}>
      <Form />
    </SimpleForm>
  </RACreate>
);

export const InsuranceEdit = (props: any) => (
  <RAEdit
    {...props}
    title={<Title />}
    redirect={false}
    mutationMode="pessimistic"
    transform={sharedTransform}>
    <SimpleForm>
      <Form />
    </SimpleForm>
  </RAEdit>
);

export const InsuranceFilter = (props: any) => {
  const translate = useTranslate();
  return (
    <RAFilter {...props}>
      <TextInput label={translate('admin.search')} source="q" alwaysOn />
    </RAFilter>
  );
};

export const InsuranceList = (props: any) => {
  const translate = useTranslate();
  return (
    <RAList
      {...props}
      filters={<InsuranceFilter />}
      filter={{
        deletedAt: { equals: null },
        // we want to show not own insurances
        isPrivate: { equals: false },
      }}
      undoable="false"
      perPage={50}
      pagination={<CustomPagination />}>
      <Datagrid>
        <TextField source="name" label={translate('admin.name')} />
        <BooleanField source="isDefault" label={translate('admin.isDefault')} />
        <FunctionField
          label={translate('admin.vehicles')}
          render={(record: VehicleInsuranceType) => record.vehicles.length}
        />
        <FunctionField
          label={translate('admin.ruleSets')}
          render={(record: VehicleInsuranceType) =>
            record.vehicleInsuranceRuleSets.length
          }
        />
        <EditButton />
        <DeleteWithConfirmButton mutationMode="pessimistic" />
      </Datagrid>
    </RAList>
  );
};
